import { Autocomplete, Box, Button, CircularProgress, Grid, TextField } from "@mui/material";
import Header from "components/Header";
import { BasePathProvider, useBasePath } from "components/Providers";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import { useDispatch, useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup"
import { useNavigate,  } from "react-router-dom";
import { saveSelf, saveStaff, patchListItem } from "store/leave/punch-request";
import { addPunchRequest } from "store/leave/punch-records";
import { useState } from "react";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import * as snackbar from '../../../../utils/snackbar'

dayjs.extend(utc);

const schema = yup.object({
  type: yup.mixed().nullable().required('Request type is required'),
  time: yup.mixed().nullable().when('type', {
    is: (val) => val?.code !== 'permission',
    then: yup.mixed().nullable().required('Time is required'),
    otherwise: yup.mixed().nullable(),
  }),
  message: yup.string().trim().required('Message is required'),
})

export default function RequestCorrectionForm({ date, selectedTeacher }) {
  const basePath = useBasePath()
  const types = useSelector(state => state.lookup.types)?.filter(type => type.category === 'punchRequestType')
  const [selectedType, setSelectedType] = useState(null)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { control, handleSubmit, formState } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { type: null, time: null, message: '' },
  })

  const onSubmit = async (data) => {
    const payload = {
      ...(selectedTeacher?.personId ? { personId: selectedTeacher.personId } : null),
      type: selectedType?.code,
      ...(date ? { date: dayjs(date).format('YYYY-MM-DD') } : null),
      ...(data.time ? { time: dayjs(data.time).format('HH:mm:ss') } : null),
      ...(data.message ? { note: data.message } : null),
    }

    try {
      const action = selectedTeacher?.personId ? saveStaff(payload) : saveSelf(payload)
      const result = await dispatch(action).unwrap()
      dispatch(addPunchRequest({
        type: selectedType?.code,
        date: dayjs(date).format('YYYY-MM-DD'),
        status: 'pending',
      }))
      if (result?.id && selectedTeacher?.person) {
        dispatch(patchListItem({
          id: result.id,
          requester: {
            firstName: selectedTeacher.person.firstName,
            lastName: selectedTeacher.person.lastName,
          },
        }))
      }
      snackbar.success('Successfully Saved')
      navigate(basePath)
    } catch {
      snackbar.error('Error Saving! Please try again later')
    }
  }

  return (
    <BasePathProvider value={`${basePath}/punch-records`}>
      <Header title={`Request Correction for ${dayjs(date).format('MMMM D, YYYY')}`} small close borderBottom />
      <Grid container p={1} component={'form'} onSubmit={handleSubmit(onSubmit)}>
        <Grid item xs={12}>
          <Controller
            name="type"
            control={control}
            render={({ field: { onChange, value }, fieldState }) => (
              <Autocomplete
                options={types}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, val) => option?.name === val?.name}
                disableClearable
                value={selectedType || value || null}
                onChange={(_, newValue) => {
                  setSelectedType(newValue);
                  onChange(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`Request Type`}
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            )}
          />
        </Grid>
        {selectedType?.code !== 'permission' && (
          <Grid item xs={12}>
            <Controller
              name="time"
              control={control}
              render={({ field: { onChange, value }, fieldState }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <TimePicker
                    label="Time"
                    value={value || null}
                    onChange={(newTime) => {
                      if (!newTime) {
                        onChange(null);
                        return
                      }
                      const selectedDate = dayjs(date);
                      const updatedDateTime = selectedDate
                        .hour(newTime.hour())
                        .minute(newTime.minute())
                        .second(0)
                        .millisecond(0);

                      onChange(updatedDateTime)
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                    sx={{ width: '100%', mb: 1 }}
                  />
                </LocalizationProvider>
              )}
            />
          </Grid>
        )}
        <Grid item xs={12} >
          <Controller
            name="message"
            control={control}
            render={({ field: { onChange, value }, fieldState }) => (
              <TextField
                label="Message"
                fullWidth
                multiline
                value={value || ""}
                onChange={onChange}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Grid>


          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '100px' }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={formState.isSubmitting || !formState.isDirty}
                startIcon={formState.isSubmitting && <CircularProgress size={20} />}
              >
                Save
              </Button>
            </Box>
          </Grid>
        
      </Grid>
    </BasePathProvider>
  )
}
